import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
} from 'react';

import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogActions from '@material-ui/core/DialogActions';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useSnackbar } from 'notistack';

import { api, getListAllAPI, getListAPI, getUserId } from '../../services';
import { STATUS_ATIVO } from '../../utils';

const styles = {
  root: {
    margin: 0,
    padding: '16px',
  },
  closeButton: {
    position: 'absolute',
    right: '8px',
    top: '8px',
    color: 'black',
  },
};

const DialogTitle = (props) => {
  const { children, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography style={styles.root} {...other}>
      <Typography variant="h6" style={{ fontWeight: 'bold' }}>
        {children}
      </Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          style={styles.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
};

const DialogoEditarProdutoExposicao = forwardRef(
  ({ handleClose = () => {} }, ref) => {
    const [open, setOpen] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const [id, setId] = useState(null);
    const [codigo, setCodigo] = useState('');
    const [produto, setProduto] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const [valueDebounce, setValueDebounce] = useState('');
    const [options, setOptions] = useState([]);

    const [carregando, setCarregando] = useState(false);
    const [salvando, setSalvando] = useState(false);

    const handleCloseDialog = () => {
      setOpen(false);
      setId(null);
      setCodigo('');
      setProduto(null);
      setInputValue('');
    };

    useImperativeHandle(ref, () => ({
      async handleOpen(idLocal) {
        setOpen(true);
        setCarregando(true);
        setId(idLocal);
        try {
          const dataEndereco = await getListAllAPI(
            'enderecos_exposicao_mercadorias',
            ['id', 'asc'],
            { id: [idLocal] },
            [],
          );
          setCodigo(dataEndereco.data[0] ? dataEndereco.data[0].codigo : '');

          const dataAndar = await getListAllAPI(
            'enderecos_exposicao_andares_produtos',
            ['id', 'asc'],
            {
              endereco_exposicao_produto_id: idLocal,
              andar: 1,
              status: STATUS_ATIVO,
            },
            ['produto'],
          );
          const atual = dataAndar.data[0] || null;
          setProduto(atual ? atual.produto : null);
          setInputValue(atual && atual.produto ? atual.produto.nome : '');
        } catch (erros) {
          enqueueSnackbar(`${erros}`, { variant: 'error' });
        } finally {
          setCarregando(false);
        }
      },
    }));

    useEffect(() => {
      const timer = setTimeout(() => setValueDebounce(inputValue), 500);
      return () => clearTimeout(timer);
    }, [inputValue]);

    useEffect(() => {
      let active = true;

      (async () => {
        const { data } = await getListAPI(
          'produtos_exposicao',
          ['id', 'asc'],
          [1, 10],
          {
            nome: valueDebounce,
            status: STATUS_ATIVO,
            is_pallet_exposicao: true,
          },
          [],
        );
        if (active) setOptions(data);
      })();

      return () => {
        active = false;
      };
    }, [valueDebounce]);

    async function handleSalvar() {
      setSalvando(true);
      try {
        const response = await api.put(
          `/enderecos_exposicao_mercadorias/produto-exposicao/${id}`,
          {
            produto_id: produto ? produto.id : null,
            user_id: getUserId(),
          },
        );
        if (response.data.status) {
          enqueueSnackbar('Produto atualizado com sucesso!', {
            variant: 'success',
          });
          handleCloseDialog();
          handleClose();
        } else {
          enqueueSnackbar(response.data.message, { variant: 'error' });
        }
      } catch (erros) {
        enqueueSnackbar(`${erros}`, { variant: 'error' });
      } finally {
        setSalvando(false);
      }
    }

    return (
      <Dialog
        open={open}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title" onClose={handleCloseDialog}>
          {`Produto em Exposição - ${codigo}`}
        </DialogTitle>
        <MuiDialogContent>
          {carregando ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '16px',
              }}
            >
              <CircularProgress size={24} />
            </div>
          ) : (
            <Autocomplete
              value={produto}
              onChange={(event, newValue) => setProduto(newValue)}
              inputValue={inputValue}
              onInputChange={(event, newInputValue) =>
                setInputValue(newInputValue)
              }
              getOptionSelected={(option, value) => option.id === value.id}
              getOptionLabel={(option) => option.nome || ''}
              options={options}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  margin="normal"
                  label="Produto"
                  autoFocus
                  fullWidth
                />
              )}
            />
          )}
        </MuiDialogContent>
        <MuiDialogActions style={{ padding: '16px' }}>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSalvar}
            disabled={carregando || salvando}
          >
            {salvando ? <CircularProgress size={20} /> : 'Salvar'}
          </Button>
        </MuiDialogActions>
      </Dialog>
    );
  },
);

export default DialogoEditarProdutoExposicao;
